/**
 * useTenantResolve — TanStack Query hook for pre-login tenant resolution.
 *
 * Calls the public `GET /tenants/resolve?host=` endpoint to map the
 * current hostname to a tenant + branding. Cached for 5 minutes.
 * Does NOT require authentication (used before login).
 */

import { useQuery } from '@tanstack/react-query';
import {
  resolveTenant,
  type TenantResolution,
} from '@/features/platform/api';
import { activeBrand } from '@brands/active';

/**
 * Resolve `host`, re-asking for `devHost` when it maps to no tenant.
 *
 * `localhost` has no `TenantDomain` row, so the login page would refuse to
 * name a tenant and show no form. In dev the brand the server was started with
 * (`yarn dev:mazeed` / `dev:nuur`) supplies its seeded host instead — outside
 * dev `devHost` is null, because an unresolved production host is a
 * misconfiguration the page must keep reporting.
 */
export async function resolveTenantWithDevFallback(
  host: string,
  devHost: string | null
): Promise<TenantResolution> {
  const resolution = await resolveTenant(host);
  if (resolution.status !== 'unmapped') return resolution;
  if (!devHost || devHost === host) return resolution;
  return resolveTenant(devHost);
}

export const devFallbackHost = (): string | null =>
  import.meta.env?.DEV ? (activeBrand.devHost ?? null) : null;

export function useTenantResolve() {
  const host = typeof window !== 'undefined' ? window.location.hostname : '';
  const devHost = devFallbackHost();

  return useQuery<TenantResolution>({
    queryKey: ['tenant-resolve', host, devHost],
    queryFn: () => resolveTenantWithDevFallback(host, devHost),
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 10,   // keep in cache 10 min
    retry: 1,
    // Don't refetch on window focus — branding is stable
    refetchOnWindowFocus: false,
    // Resolve even before login
    enabled: !!host,
  });
}
